import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CheckCircle2, Download, FileCheck2, RefreshCw,
  ShieldAlert, XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { adminApi, getApiErrorMessage } from "@/lib/api";

const tone = (status) => {
  if (["PASS", "IMPLEMENTED"].includes(status)) return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (["FAIL", "ENDPOINT_GAP", "POLICY_BLOCKED"].includes(status)) return "border-red-200 bg-red-50 text-red-800";
  if (status === "AUTHZ_GAP") return "border-orange-200 bg-orange-50 text-orange-900";
  return "border-amber-200 bg-amber-50 text-amber-900";
};

function Pill({ status }) {
  return <span className={`inline-flex shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold tracking-wide ${tone(status)}`}>{String(status || "UNKNOWN").replaceAll("_", " ")}</span>;
}

export default function BseUatDossier() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.get("/admin/bse2/uat-dossier");
      setData(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setDownloading(true);
    try {
      const response = await adminApi.get("/admin/bse2/uat-dossier/pdf", {
        responseType: "blob",
        timeout: 180_000,
      });
      if (!(response.headers["content-type"] || "").includes("application/pdf")) {
        throw new Error("The server did not return a BSE UAT PDF.");
      }
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || "SMIFS-BSE-StAR-MF-2.0-UAT-Dossier.pdf";
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("BSE StAR MF 2.0 UAT dossier downloaded");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  const dossier = data?.dossier || {};
  const snapshot = data?.snapshot || {};
  const run = snapshot.run || {};
  const matrix = useMemo(() => snapshot.endpoint_matrix || [], [snapshot.endpoint_matrix]);
  const grouped = useMemo(() => matrix.reduce((out, row) => {
    (out[row.module] ||= []).push(row);
    return out;
  }, {}), [matrix]);

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--cta)]">
            <FileCheck2 className="h-4 w-4" /> BSE go-live evidence
          </div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">BSE StAR MF 2.0 UAT dossier</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--ink-muted)]">
            BSE-only submission document mapping each endpoint to its request builder, application call,
            validation, persistence, reconciliation and PII-safe UAT evidence.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh evidence
          </Button>
          <Button onClick={download} disabled={loading || downloading || !data}>
            <Download className="mr-2 h-4 w-4" />{downloading ? "Building dossier…" : "Download BSE PDF"}
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div><strong>Submission position: application ready, external closures required.</strong> Zero application/transport failures does not convert BSE member-authorization, endpoint or investor-authentication gaps into passes. The PDF requests those closures explicitly.</div>
        </div>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {[
          ["UAT steps", run.total ?? "—"], ["Passed", run.passed ?? "—"],
          ["BSE validations", run.status_counts?.REACHED_VALIDATION ?? "—"],
          ["Authorization gaps", run.status_counts?.AUTHZ_GAP ?? "—"],
          ["Endpoint gaps", run.status_counts?.ENDPOINT_GAP ?? "—"],
          ["Application failures", run.failed ?? "—"],
        ].map(([label, value]) => (
          <Card key={label} className="border-[var(--glass-border)]">
            <CardContent className="p-4">
              <div className="text-xs text-[var(--ink-muted)]">{label}</div>
              <div className="mt-1 font-heading text-2xl text-[var(--brand)]">{loading ? "…" : value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mb-6 border-[var(--glass-border)]">
        <CardHeader><CardTitle>Controlled evidence record</CardTitle></CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
          {[
            ["Run ID", run.run_id], ["Database evidence ID", run.persistence_id],
            ["Executed", run.at ? new Date(run.at).toLocaleString() : "No full run"],
            ["BSE member", snapshot.member_code], ["BSE mode", snapshot.mode],
            ["Current environment", snapshot.current_environment],
            ["Regular BSE schemes", snapshot.regular_schemes?.toLocaleString()],
            ["Direct scheme rows", snapshot.direct_schemes?.toLocaleString()],
          ].map(([label, value]) => (
            <div key={label} className="min-w-0 rounded-xl bg-[var(--surface-2)] p-3">
              <div className="text-xs text-[var(--ink-muted)]">{label}</div>
              <div className="mt-1 break-all font-medium text-[var(--ink)]">{value ?? "—"}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Card className="border-orange-200">
          <CardHeader><CardTitle className="flex items-center gap-2 text-orange-900"><ShieldAlert className="h-5 w-5" />BSE action requested</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm leading-6 text-orange-900">
            <div>1. Enable/clarify member-PG detail and list-payment permissions.</div>
            <div>2. Enable/clarify Payment Aggregator MIS upload, lookup and reversal.</div>
            <div>3. Confirm NFT bank, contact and nominee UAT/production paths.</div>
            <div>4. Provide/approve UAT identities for KYC and investor-auth completion.</div>
            <div>5. Confirm JOSE certificates, production entitlement and webhook onboarding.</div>
          </CardContent>
        </Card>
        <Card className={run.failed === 0 ? "border-emerald-200" : "border-red-300"}>
          <CardHeader><CardTitle className="flex items-center gap-2">{run.failed === 0 ? <CheckCircle2 className="h-5 w-5 text-emerald-700" /> : <XCircle className="h-5 w-5 text-red-700" />}Application gate</CardTitle></CardHeader>
          <CardContent className="text-sm leading-6 text-[var(--ink)]">
            {run.failed === 0
              ? "The persisted final matrix contains zero application or transport failures. BSE business validations and external gaps remain visible and must be closed or accepted before LIVE."
              : "The latest persisted run contains an application/transport failure and must not be submitted as go-live evidence."}
            <div className="mt-3 text-xs text-[var(--ink-muted)]">{dossier.endpoint_contracts || 0} logical endpoint contracts · {dossier.word_count?.toLocaleString() || 0} mapped words · source {dossier.official_reference || "—"}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[var(--glass-border)]">
        <CardHeader>
          <CardTitle>Endpoint coverage and mapping</CardTitle>
          <p className="text-sm text-[var(--ink-muted)]">The PDF expands every row into purpose, code wrapper, caller, request fields, validations, persistence and response behavior.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          {Object.entries(grouped).map(([module, rows]) => (
            <section key={module}>
              <h2 className="mb-2 text-sm font-semibold text-[var(--brand)]">{module}</h2>
              <div className="space-y-2">
                {rows.map((row) => (
                  <div key={`${module}-${row.key}`} className="flex flex-col gap-2 rounded-xl border border-[var(--glass-border)] p-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="font-medium text-[var(--ink)]">{row.key}</div>
                      <div className="mt-1 break-all font-mono text-[11px] text-[var(--ink-muted)]">POST {row.path}</div>
                      <div className="mt-1 text-xs leading-5 text-[var(--ink-muted)]">{row.purpose}</div>
                    </div>
                    <Pill status={row.uat_status} />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
